import type { ModuleStudioRepository } from '../data/ModuleStudioRepository';
import type { DesignModuleSpecJson } from '../data/contracts';
import { unprovenPages } from '../template/restartFromCapture';
import { lostSections, type LostSection } from '../template/lostSections';
import { currentModel } from '../simulation/liveSimulationStep';
import { nativeConversionGaps } from '../template/nativeConversionGaps';

/** E2E-FIX1 (H, I, J, K) — avant de présenter « fais la vN », le plan lit l'état réel du dossier (import vivant, modèle courant) et
 * insère les étapes que la recette réelle du 28.10 a montrées manquantes (DIAG-E2E-FIN) :
 * - I : « Capturer les écrans » avant Compiler quand l'import vivant n'a aucune capture (prototype rendu par script : rien à compiler) ;
 * - J : « Repartir de la capture » avant Compiler quand des pages sont des acquis non prouvés (adoption à origine indisponible) ;
 * - H : « Déclarer les référentiels » (et la revue de leurs protections) avant Charger la simulation quand le modèle a des objets de
 *   référence sans jeu déclaré.
 * Il liste aussi les gestes en attente du Maker (doublons d'import, pages à repartir de la capture, protections à revoir).
 * Lecture seule ; aucune règle propre à un module. */
export const CAPTURE_SCREENS = 'capture-screens';
export const RESTART_FROM_CAPTURE = 'restart-from-capture';
export const RESTORE_SECTIONS = 'restore-sections';
/** K : versions antérieures acceptées ou remplacées (GAP-30) relues pour trouver les sections natives perdues (bornées). */
export const LOST_SECTION_VERSIONS = 5;
export type PlanGesture =
  | { kind: 'captures'; importId: string }
  | { kind: 'recapture'; pages: string[] }
  | { kind: 'references'; entities: string[] }
  | { kind: 'duplicates'; imports: string[] }
  | { kind: 'sections'; sections: LostSection[] }
  /** D10 : écarts natifs restants (candidats non liés) : le Maker peut lancer « Auditer les écarts natifs », geste séparé. */
  | { kind: 'audit'; pages: string[]; count: number };
export type Preflight = { keys: string[]; gestures: PlanGesture[] };

const before = (keys: string[], key: string, anchors: string[]) => {
  if (keys.includes(key)) return keys;
  const at = keys.findIndex(item => anchors.includes(item));
  return at < 0 ? keys : [...keys.slice(0, at), key, ...keys.slice(at)];
};
export function referencesPending(spec: DesignModuleSpecJson | null): string[] {
  if (!spec) return [];
  const data = (spec.referenceData ?? {}) as Record<string, unknown>;
  return Object.entries((spec.entities ?? {}) as Record<string, { kind?: string }>)
    .filter(([code, entity]) => entity.kind === 'reference' && !(Array.isArray(data[code]) && (data[code] as unknown[]).length))
    .map(([code]) => code);
}

export async function preflightPlan(repository: ModuleStudioRepository, dossier: string, keys: string[], available: (key: string) => boolean): Promise<Preflight> {
  let next = [...keys];
  const gestures: PlanGesture[] = [];
  if (typeof repository.listPrototypeImports === 'function' && typeof repository.getPrototypeImport === 'function') {
    const imports = (await repository.listPrototypeImports(dossier)).filter(item => item.status === 'imported' || item.status === 'analysed');
    const details = await Promise.all(imports.map(item => repository.getPrototypeImport(item.id)));
    // Recette du 28.10 : imports 12 et 13, même octets à 28 s d'écart.
    const bySha = new Map<string, string[]>();
    details.forEach((detail, index) => {
      const sha = detail.pieces.filter(piece => piece.kind === 'html').map(piece => piece.sha256).sort().join('|');
      if (sha) bySha.set(sha, [...(bySha.get(sha) ?? []), imports[index].id]);
    });
    for (const ids of bySha.values()) if (ids.length > 1) gestures.push({ kind: 'duplicates', imports: ids });
    const live = details[0];
    if (live && next.includes('compile') && available(CAPTURE_SCREENS) && live.pieces.some(piece => piece.kind === 'html') && !live.pieces.some(piece => piece.kind === 'snapshot')) {
      next = before(next, CAPTURE_SCREENS, ['compile']);
      gestures.push({ kind: 'captures', importId: imports[0].id });
    }
  }
  const { spec } = await currentModel(repository, dossier);
  const unproven = spec ? unprovenPages(spec).map(item => item.page) : [];
  if (unproven.length && next.includes('compile') && available(RESTART_FROM_CAPTURE)) {
    next = before(next, RESTART_FROM_CAPTURE, ['compile']);
    gestures.push({ kind: 'recapture', pages: unproven });
  }
  // K : native sections the Maker added in an earlier accepted version and the current model lost (not in any capture).
  if (spec && next.includes('compile') && available(RESTORE_SECTIONS) && typeof repository.listModuleSpecVersions === 'function') {
    // GAP-30 (passe réelle n° 3) : une version acceptée puis remplacée (« superseded ») reste une version du Maker : la v30 de Finances,
    // qui porte « Membres du foyer », est remplacée par la v31. Les versions refusées ou seulement proposées ne sont jamais relues.
    const versions = (await repository.listModuleSpecVersions(dossier)).filter(item => item.status === 'accepted' || item.status === 'superseded').sort((a, b) => b.version - a.version).slice(0, LOST_SECTION_VERSIONS);
    const found: LostSection[] = [];
    for (const item of versions) {
      const older = (await repository.getModuleSpecVersion(dossier, item.version)).spec;
      for (const section of lostSections(spec, older, item.version)) if (!found.some(known => known.page === section.page && known.heading === section.heading)) found.push(section);
    }
    if (found.length) {
      next = before(next, RESTORE_SECTIONS, ['declare-references', 'load-simulation', 'bind', 'propose']);
      gestures.push({ kind: 'sections', sections: found });
    }
  }
  const references = referencesPending(spec);
  if (references.length && (next.includes('load-simulation') || next.includes('bind')) && available('declare-references')) {
    next = before(next, 'declare-references', ['load-simulation', 'bind']);
    gestures.push({ kind: 'references', entities: references });
  }
  // D10 (décision d'Olivier du 29.10) : l'audit Maker n'est jamais inséré d'office ; quand le modèle courant a encore des écarts
  // natifs (candidats non liés), il est proposé comme geste en attente, à lancer séparément (« Auditer les écarts natifs »).
  if (spec && (next.includes('bind') || next.includes('propose'))) {
    const gaps = nativeConversionGaps(spec);
    const count = gaps.reduce((sum, gap) => sum + gap.count, 0);
    if (count > 0) gestures.push({ kind: 'audit', pages: [...new Set(gaps.map(gap => gap.page))], count });
  }
  return { keys: next, gestures };
}
